import { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { extractError } from "@/lib/api";
import { toast } from "sonner";
import { HeartHandshake, ShieldCheck, Sparkles, ArrowRight, Home } from "lucide-react";

export default function LoginPage() {
  const { login } = useAuth();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("logout") === "1") {
      toast.success("تم تسجيل الخروج بنجاح", { duration: 3000 });
      // Clean the URL so a refresh doesn't re-toast
      window.history.replaceState({}, "", "/login");
    }
  }, [location.search]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try { await login(email.trim(), password); }
    catch (e) { setErr(extractError(e)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen relative overflow-hidden" data-testid="login-page">
      {/* Vibrant background */}
      <div className="absolute inset-0 bg-gradient-to-br from-sky-100 via-white to-amber-50" />
      <div className="absolute top-0 left-0 w-96 h-96 rounded-full bg-gradient-to-br from-pink-300 to-rose-400 opacity-30 blur-3xl -translate-x-1/3 -translate-y-1/3" />
      <div className="absolute top-1/3 right-0 w-[500px] h-[500px] rounded-full bg-gradient-to-br from-sky-300 to-cyan-400 opacity-30 blur-3xl translate-x-1/3" />
      <div className="absolute bottom-0 left-1/4 w-96 h-96 rounded-full bg-gradient-to-br from-emerald-300 to-teal-400 opacity-30 blur-3xl translate-y-1/3" />
      <div className="absolute bottom-1/4 right-1/4 w-72 h-72 rounded-full bg-gradient-to-br from-amber-300 to-yellow-400 opacity-25 blur-3xl" />

      {/* Floating decorative shapes */}
      <div className="absolute top-20 right-16 w-16 h-16 bg-gradient-to-br from-yellow-300 to-orange-400 rounded-3xl rotate-12 shadow-xl animate-pulse" />
      <div className="absolute top-40 left-24 w-12 h-12 bg-gradient-to-br from-pink-300 to-rose-400 rounded-full shadow-xl" />
      <div className="absolute bottom-32 right-32 w-14 h-14 bg-gradient-to-br from-violet-300 to-purple-400 rounded-2xl -rotate-12 shadow-xl" />
      <div className="absolute bottom-20 left-16 w-10 h-10 bg-gradient-to-br from-emerald-300 to-teal-400 rounded-full shadow-xl" />

      <div className="relative min-h-screen grid lg:grid-cols-2">
        {/* Left / Info */}
        <div className="hidden lg:flex flex-col justify-between p-14">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Happy Day" className="w-16 h-16 rounded-3xl shadow-2xl shadow-sky-200 bg-white p-1.5" />
            <div>
              <div className="text-2xl font-black text-slate-900">مركز Happy Day</div>
              <div className="text-sm text-slate-600">منصة إدارة وتواصل متكاملة</div>
            </div>
          </div>

          <div className="space-y-8">
            <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur border border-sky-200 rounded-full px-4 py-1.5 text-sm font-bold text-sky-700 shadow-sm">
              <Sparkles className="w-4 h-4" /> بيئة تعليمية ملهمة
            </div>
            <h1 className="text-4xl xl:text-5xl font-black text-slate-900 leading-tight">
              رعاية منظمة.
              <br />
              <span className="bg-gradient-to-l from-sky-600 via-blue-600 to-violet-600 bg-clip-text text-transparent">تواصل واضح.</span>
              <br />
              خصوصية محفوظة.
            </h1>
            <p className="text-lg text-slate-700 max-w-md leading-relaxed">
              نظام واحد يجمع الإدارة والمعلمين وأولياء الأمور — بديلٌ حقيقي لفوضى مجموعات الواتساب،
              مع الحفاظ على إشعارات الواتساب كقناة أساسية للأهل.
            </p>
            <div className="grid grid-cols-2 gap-4 max-w-md">
              <FeaturePill icon={<ShieldCheck className="w-5 h-5" />} title="صلاحيات صارمة" desc="كل ولي أمر يرى طفله فقط" tone="sky" />
              <FeaturePill icon={<HeartHandshake className="w-5 h-5" />} title="متابعة دقيقة" desc="سجل شامل لكل يوم" tone="emerald" />
            </div>
          </div>

          <div className="flex items-center justify-between text-xs text-slate-500">
            <span>© {new Date().getFullYear()} Happy Day Center. جميع الحقوق محفوظة.</span>
            <Link to="/welcome" className="inline-flex items-center gap-1 text-sky-700 font-bold hover:text-sky-800">
              <Home className="w-3 h-3" /> صفحة المركز
            </Link>
          </div>
        </div>

        {/* Right / Form */}
        <div className="flex items-center justify-center p-6 lg:p-14">
          <Card className="w-full max-w-md shadow-2xl shadow-sky-100/50 border-2 border-white/60 backdrop-blur-sm bg-white/95">
            <CardContent className="p-8 md:p-10 space-y-6">
              <div className="lg:hidden flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <img src="/logo.png" alt="Happy Day" className="w-12 h-12 rounded-2xl bg-white p-1 shadow-lg" />
                  <div className="text-xl font-black text-slate-900">Happy Day</div>
                </div>
                <Link to="/welcome" className="text-xs font-bold text-sky-700 inline-flex items-center gap-1">
                  <Home className="w-3 h-3" /> عن المركز
                </Link>
              </div>

              <div>
                <div className="inline-flex items-center gap-2 bg-sky-50 border border-sky-200 rounded-full px-3 py-1 text-xs font-bold text-sky-700 mb-3">
                  <ShieldCheck className="w-3.5 h-3.5" /> بوابة آمنة
                </div>
                <h2 className="text-3xl font-black text-slate-900">أهلاً بعودتك 👋</h2>
                <p className="text-slate-500 mt-1">أدخل بريدك الإلكتروني وكلمة المرور</p>
              </div>

              <form onSubmit={submit} className="space-y-5">
                <div className="space-y-2">
                  <Label className="text-base font-bold text-slate-800">البريد الإلكتروني</Label>
                  <Input
                    data-testid="login-email"
                    type="email"
                    dir="ltr"
                    className="h-12 text-base border-2 focus:border-sky-500"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-base font-bold text-slate-800">كلمة المرور</Label>
                  <Input
                    data-testid="login-password"
                    type="password"
                    className="h-12 text-base border-2 focus:border-sky-500"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>

                {err && (
                  <div data-testid="login-error" className="rounded-xl bg-rose-50 border-2 border-rose-200 text-rose-700 px-4 py-3 text-sm font-bold">
                    {err}
                  </div>
                )}

                <Button
                  data-testid="login-submit"
                  type="submit"
                  disabled={loading}
                  className="w-full h-14 text-base bg-gradient-to-l from-sky-600 via-blue-600 to-violet-600 hover:from-sky-700 hover:via-blue-700 hover:to-violet-700 text-white font-black rounded-2xl shadow-xl shadow-sky-200 transition-all gap-2"
                >
                  {loading ? "جاري الدخول..." : (<><span>دخول</span> <ArrowRight className="w-5 h-5 rotate-180" /></>)}
                </Button>
              </form>

              <div className="text-center text-xs text-slate-500 pt-2 border-t border-slate-100">
                لا تملك حساباً؟ تواصل مع إدارة المركز للتسجيل
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

function FeaturePill({ icon, title, desc, tone = "sky" }) {
  const tones = {
    sky: "bg-sky-100 text-sky-700 border-sky-200",
    emerald: "bg-emerald-100 text-emerald-700 border-emerald-200",
  };
  return (
    <div className="p-4 rounded-2xl bg-white/80 backdrop-blur border-2 border-white shadow-lg">
      <div className={`w-10 h-10 rounded-xl ${tones[tone]} border flex items-center justify-center mb-2`}>{icon}</div>
      <div className="font-black text-slate-800">{title}</div>
      <div className="text-sm text-slate-500">{desc}</div>
    </div>
  );
}
